<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import StatusBar from '@/V2/components/status/StatusBar.vue';
import { Statuses } from '@/V2/components/status/utils';
import {
  iButton,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
} from '@inscribe/signal';
import { riskLevelToReadableLabel } from '@/constants/riskLevels';
import { RISK_LEVEL } from '@/constants/riskLevels';

export default {
  components: {
    ExpandableContainer,
    iButton,
    StatusBar,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    summary: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      Statuses,
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      riskLevelToReadableLabel,
      RISK_LEVEL,
    };
  },
  computed: {
    hasEmptyState() {
      return !this.summary?.aggregated_risk_counts;
    },
    showEmptyState() {
      return this.loading || this.hasEmptyState;
    },
    displayedRiskLevels() {
      const riskLevels = [RISK_LEVEL.HIGH, RISK_LEVEL.MEDIUM, RISK_LEVEL.LOW];
      if (
        this.summary?.aggregated_risk_counts?.[RISK_LEVEL.NOT_APPLICABLE] > 0
      ) {
        riskLevels.push(RISK_LEVEL.NOT_APPLICABLE);
      }
      return riskLevels;
    },
  },
  methods: {
    getBarWidth(riskKey) {
      const total = Object.values(
        this.summary?.aggregated_risk_counts || {},
      ).reduce((acc, curr) => acc + curr, 0);
      return total === 0
        ? 0
        : parseInt(
            (this.summary?.aggregated_risk_counts[riskKey] / total) * 100,
          );
    },
    onViewDocumentsClick(riskKey) {
      this.$router.push({
        name: 'customer-documents',
        params: {
          customerId: this.$route.params.customerId,
        },
        query: {
          risk_levels: riskKey ? [riskKey] : undefined,
        },
      });
    },
  },
};
</script>

<template>
  <div class="customer-fraud-summary">
    <ExpandableContainer title="Document Distribution" :expandable="false">
      <template #tools>
        <i-button
          v-if="!showEmptyState"
          text="View Documents"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.OUTLINE"
          :disabled="loading"
          @button-click="onViewDocumentsClick()"
          data-dd-action-name="customer-view-documents-distribution-view-documents-button"
        />
      </template>

      <template #items>
        <div class="distribution slot-content d-flex flex-column i-p-4 i-gap-2">
          <button
            class="bar d-flex flex-column i-gap-4 i-px-6 i-pb-6 i-pt-4 i-radius-md"
            v-for="riskKey in displayedRiskLevels"
            :key="riskKey"
            :data-testid="`customer-fraud-distribution-bar-${riskKey}`"
            @click="onViewDocumentsClick(riskKey)"
            data-dd-action-name="customer-view-documents-distribution-view-by-risk-level-button"
          >
            <div class="bar-label d-flex justify-content-between i-radius-md">
              <span class="label i-text-subheading-2 i-text-neutral-120">{{
                riskLevelToReadableLabel(riskKey)
              }}</span>
              <div class="d-flex flex-row align-items-center i-gap-6">
                <span class="view-documents i-text-subheading-2">View</span>
                <span class="value i-text-subheading-2 i-text-neutral-120">{{
                  showEmptyState
                    ? '0'
                    : summary?.aggregated_risk_counts[riskKey]
                }}</span>
              </div>
            </div>
            <div
              :class="{
                'linear-background': loading,
              }"
              class="bar-fill i-radius-sm"
            >
              <div
                v-if="!showEmptyState"
                :class="{
                  high: riskKey === RISK_LEVEL.HIGH,
                  medium: riskKey === RISK_LEVEL.MEDIUM,
                  low: riskKey === RISK_LEVEL.LOW,
                  'not-applicable': riskKey === RISK_LEVEL.NOT_APPLICABLE,
                }"
                class="fill i-radius-sm"
                :style="{ width: getBarWidth(riskKey) + '%' }"
              ></div>
            </div>
          </button>
        </div>
      </template>
    </ExpandableContainer>
  </div>
</template>

<style lang="scss" scoped>
.distribution {
  min-height: 196px;
  background: $white-base;
  height: 100%;

  .bar {
    background: var(--base);
    border: 0;
    transition: background-color 150ms ease-in-out;

    &:hover {
      background-color: var(--surface-neutral-subtle);

      .view-documents {
        opacity: 1;
      }
    }

    .view-documents {
      color: var(--text-neutral);
      opacity: 0;
      transition: opacity 150ms ease-in-out;
    }

    .bar-fill {
      position: relative;
      height: 8px;
      width: 100%;
      background-color: $light-neutral-30;

      .fill {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        &.high {
          background-color: $light-error-90;
        }

        &.medium {
          background-color: $light-warning-90;
        }

        &.low {
          background-color: $light-success-90;
        }

        &.not-applicable {
          background-color: $light-neutral-90;
        }
      }
    }
  }
}
</style>
